import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";import { toast } from "sonner";
import {
  ShieldCheck,
  Calculator,
  Sparkles,
  Trophy,
  AlertTriangle,
  Lock,
  CheckCircle2,
  ExternalLink,
  Users,
  Target,
  Save,
  RotateCcw,
  Zap,
} from "lucide-react";

/**
 * /admin/seeding — Super Admin Category-Points Seeding Editor.
 *
 * Season 3 mandate: A+=1, A=2, B=3, C=4, D=5, E=6 category points.
 * Auto-seed rule: LOWEST pair total = Seed 1, then Seed 2, then Seed 3.
 * Pair composition uses balanced-strength pairing (strongest + weakest, etc.)
 * so pair totals are roughly equal BEFORE ranking.
 *
 * Workflow:
 *   1. Load every upcoming/scheduled league tie.
 *   2. Pick a tie → both team panels render side-by-side.
 *   3. For each team, pick exactly 6 players from the roster.
 *   4. "Auto-Compute Seeds" hits POST /api/lineups/auto-seed.
 *   5. "Publish Override" hits POST /api/lineups/{tie_id}/override with mandatory reason.
 *
 * All state is per-tie + per-team; nothing is persisted server-side until
 * "Publish Override" is clicked.
 */

const CATEGORY_POINTS = { "A+": 1, A: 2, B: 3, C: 4, D: 5, E: 6 };
const pts = (p) => CATEGORY_POINTS[(p?.category || "").toUpperCase()] ?? 6;

function PlayerPill({ p, selected, onToggle, disabled }) {
  const cp = pts(p);
  return (
    <button
      onClick={() => !disabled && onToggle(p.id)}
      disabled={disabled}
      data-testid={`seeding-player-toggle-${p.id}`}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition text-left ${
        selected
          ? "bg-[#F4B400]/15 border-[#F4B400] text-white"
          : "bg-black/30 border-white/10 hover:border-white/40 text-zinc-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center ${
            selected ? "bg-[#F4B400] text-black" : "bg-white/10 text-zinc-300"
          }`}
        >
          {cp}
        </span>
        <span className="text-sm">{p.name}</span>
        {p.is_international && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 font-bold uppercase">
            INTL
          </span>
        )}
      </div>
      <span className="text-[10px] font-bold uppercase text-zinc-500">
        {p.category || "—"}
      </span>
    </button>
  );
}

function SeedPairPreview({ label, ids, players, total, tied }) {
  const byId = useMemo(() => Object.fromEntries((players || []).map((p) => [p.id, p])), [players]);
  const items = (ids || []).map((id) => byId[id] || { id, name: "?" });
  return (
    <div
      className={`p-3 rounded-lg border ${
        tied
          ? "border-amber-500/60 bg-amber-500/10"
          : "border-emerald-500/40 bg-emerald-500/5"
      }`}
      data-testid={`seeding-preview-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">
          {label}
        </div>
        <div className="font-anton text-2xl tabular-nums text-[#F4B400]">
          {total ?? "—"}
        </div>
      </div>
      {items.map((p) => (
        <div key={p.id} className="flex items-center justify-between text-sm py-0.5 text-zinc-100">
          <span>{p.name}</span>
          <span className="text-[10px] font-bold text-zinc-500">{pts(p)} pt</span>
        </div>
      ))}
    </div>
  );
}

function TeamPanel({ tieId, team, matches, onPublished }) {
  const [roster, setRoster] = useState([]);
  // Structured slot state (iter78): admin can manually pick each seat.
  const emptySlots = { seed_1: ["", ""], seed_2: ["", ""], seed_3: ["", ""], reserve: "" };
  const [slots, setSlots] = useState(emptySlots);
  // iter80 · Change-detection baseline: whatever we first loaded from the
  // server (or reset to empty). `hasChanged` compares this by VALUE — pair
  // order within a seed is irrelevant, only the two player IDs matter.
  const [originalSlots, setOriginalSlots] = useState(emptySlots);
  const [autoResult, setAutoResult] = useState(null);
  const [existingLineup, setExistingLineup] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/players?team_id=${team.id}`);
        if (!cancelled) setRoster(data || []);
        try {
          const { data: full } = await api.get(`/lineups/${tieId}`);
          const mine = (full.lineups || []).find((l) => l.team_id === team.id);
          if (!cancelled) {
            setExistingLineup(mine || null);
            if (mine) {
              const usedIds = new Set();
              const pick = (arr) =>
                (arr || []).map((p) => {
                  usedIds.add(p.id);
                  return p.id;
                });
              const s1 = pick(mine.seed_1);
              const s2 = pick(mine.seed_2);
              const s3 = pick(mine.seed_3);
              const reserve = (data || []).find((p) => !usedIds.has(p.id))?.id || "";
              const loaded = {
                seed_1: [s1[0] || "", s1[1] || ""],
                seed_2: [s2[0] || "", s2[1] || ""],
                seed_3: [s3[0] || "", s3[1] || ""],
                reserve,
              };
              setSlots(loaded);
              setOriginalSlots(loaded);
            } else {
              setOriginalSlots(emptySlots);
            }
          }
        } catch {
          /* super admin — no 403 expected; ignore */
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to load ${team.name} roster`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tieId, team.id]);

  const selected = useMemo(
    () => [...slots.seed_1, ...slots.seed_2, ...slots.seed_3].filter(Boolean),
    [slots]
  );
  const totalFilled = selected.length;

  /** True if the current slot state differs from the last-loaded baseline.
   * Compares by VALUE (sorted pair IDs) so swapping the two players inside
   * a single seed pair doesn't count as a change. */
  const hasChanged = useMemo(() => {
    const canon = (s) => ({
      seed_1: [...s.seed_1].sort(),
      seed_2: [...s.seed_2].sort(),
      seed_3: [...s.seed_3].sort(),
      reserve: s.reserve || "",
    });
    return JSON.stringify(canon(slots)) !== JSON.stringify(canon(originalSlots));
  }, [slots, originalSlots]);

  const setSlot = (slotKey, idx, newId) => {
    setAutoResult(null);
    setSlots((prev) => {
      const next = {
        seed_1: [...prev.seed_1],
        seed_2: [...prev.seed_2],
        seed_3: [...prev.seed_3],
        reserve: prev.reserve,
      };
      if (slotKey === "reserve") {
        // If newId already in another seed, swap current reserve into that slot.
        for (const k of ["seed_1", "seed_2", "seed_3"]) {
          const swapIdx = next[k].indexOf(newId);
          if (swapIdx !== -1) next[k][swapIdx] = prev.reserve || "";
        }
        next.reserve = newId;
      } else {
        // If newId is in another seed slot, remove it there.
        for (const k of ["seed_1", "seed_2", "seed_3"]) {
          if (k === slotKey) continue;
          const swapIdx = next[k].indexOf(newId);
          if (swapIdx !== -1) next[k][swapIdx] = "";
        }
        if (next.reserve === newId) next.reserve = "";
        next[slotKey][idx] = newId;
      }
      return next;
    });
  };

  const clearAll = () => setSlots(emptySlots);

  const compute = async () => {
    if (totalFilled !== 6) {
      toast.error(`Fill all 6 seed slots first (${totalFilled}/6 filled).`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/lineups/auto-seed", {
        team_id: team.id,
        player_ids: selected,
      });
      setAutoResult(data);
      // Auto-fill the slots with the computed order.
      setSlots({
        seed_1: [data.seed_1[0] || "", data.seed_1[1] || ""],
        seed_2: [data.seed_2[0] || "", data.seed_2[1] || ""],
        seed_3: [data.seed_3[0] || "", data.seed_3[1] || ""],
        reserve: slots.reserve,
      });
      if (data.tied) {
        toast.warning(`Tied: ${data.tied}. Adjust manually if needed.`);
      } else {
        toast.success("Auto-seed computed — slots updated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-seed failed");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (totalFilled !== 6) {
      toast.error(`All 6 seed slots must be filled (${totalFilled}/6).`);
      return;
    }
    if (
      slots.seed_1.some((x) => !x) ||
      slots.seed_2.some((x) => !x) ||
      slots.seed_3.some((x) => !x)
    ) {
      toast.error("Every seed pair must have exactly 2 players.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Override reason is required (audit trail).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/lineups/${tieId}/override`, {
        team_id: team.id,
        seed_1: slots.seed_1,
        seed_2: slots.seed_2,
        seed_3: slots.seed_3,
        reason: reason.trim(),
        // Preserve the admin's manual ordering — do NOT server-side re-sort.
        bypass_auto_seed: true,
      });
      toast.success(`Lineup published · locked=${data.locked}`);
      setReason("");
      // iter80 · reset the change-detection baseline to what we just saved so
      // the Publish button correctly re-disables until another change is made.
      setOriginalSlots({
        seed_1: [...slots.seed_1],
        seed_2: [...slots.seed_2],
        seed_3: [...slots.seed_3],
        reserve: slots.reserve,
      });
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const nameOf = (id) => (roster.find((p) => p.id === id) || {}).name || "?";
  const totalOf = (ids) =>
    ids.filter(Boolean).reduce((sum, id) => sum + pts(roster.find((p) => p.id === id)), 0);

  /** Where is each player currently assigned? Used to annotate (not disable)
   * options so the Super Admin can still pick anyone — selection performs a
   * clean swap via `setSlot`. */
  const assignedLabelFor = (pid) => {
    if (slots.seed_1.includes(pid)) return "Seed 1";
    if (slots.seed_2.includes(pid)) return "Seed 2";
    if (slots.seed_3.includes(pid)) return "Seed 3";
    if (slots.reserve === pid) return "Reserve";
    return "";
  };

  /** Options for a specific slot: full roster, every player selectable. If a
   * player is already assigned elsewhere we ANNOTATE (not disable) so the
   * admin can still pick them — the change swaps them into the new slot. */
  const optionsFor = (currentId) =>
    roster.map((p) => {
      const assigned = assignedLabelFor(p.id);
      return {
        id: p.id,
        name: p.name,
        cat: p.category,
        pts: pts(p),
        isIntl: (p.category || "").toUpperCase() === "A+" || p.is_international,
        assignedIn: p.id !== currentId ? assigned : "",
      };
    });

  return (
    <Card
      className="p-5 bg-[#0f1a15] border-white/10"
      data-testid={`seeding-team-panel-${team.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/10"
            style={{ backgroundColor: team.color || "#00A86B22" }}
          >
            <Users className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="font-anton text-2xl uppercase text-white leading-tight">
              {team.name}
            </div>
            <Link
              to={`/teams/${team.id}`}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#F4B400] flex items-center gap-1"
            >
              Team Details <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filled</div>
          <div
            className={`font-anton text-3xl tabular-nums ${
              totalFilled === 6 ? "text-[#F4B400]" : "text-zinc-300"
            }`}
            data-testid={`seeding-selected-count-${team.id}`}
          >
            {totalFilled}/6
          </div>
        </div>
      </div>

      {existingLineup && (
        <div className="mb-3 p-2 rounded bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-xs text-emerald-100">
          <CheckCircle2 className="w-3.5 h-3.5" /> Existing lineup submitted
          {existingLineup.locked && (
            <span className="ml-2 flex items-center gap-1 text-amber-300">
              <Lock className="w-3 h-3" /> LOCKED — override still allowed
            </span>
          )}
          {existingLineup.override_reason && (
            <span className="ml-2 text-[10px] italic text-zinc-400">
              (last override: {existingLineup.override_reason})
            </span>
          )}
        </div>
      )}
      {existingLineup?.locked && (
        <div
          className="mb-3 p-2 rounded bg-red-500/10 border border-red-500/40 flex items-start gap-1.5 text-[11px] text-red-100"
          data-testid={`seeding-locked-warning-${team.id}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <b>This lineup is locked.</b> You are making a Super Admin override. Both teams have
            already submitted and been revealed — the change will be audit-logged with your reason.
          </span>
        </div>
      )}

      {/* Manual seed slot editor — iter78 */}
      <div className="space-y-3 mb-3">
        {["seed_1", "seed_2", "seed_3"].map((slotKey, si) => {
          const label = ["Seed 1", "Seed 2", "Seed 3"][si];
          const accent = ["#F4B400", "#00A86B", "#7DD3FC"][si];
          const total = totalOf(slots[slotKey]);
          const complete = slots[slotKey].every(Boolean);
          return (
            <div key={slotKey} data-testid={`seeding-slot-${team.id}-${slotKey}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div
                  className="text-[10px] uppercase tracking-widest font-bold"
                  style={{ color: accent }}
                >
                  {label} Pair
                </div>
                {complete && (
                  <div className="text-[10px] uppercase text-[#F4B400] font-bold">
                    Total <span className="text-sm tabular-nums">{total}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[0, 1].map((idx) => (
                  <select
                    key={idx}
                    value={slots[slotKey][idx] || ""}
                    onChange={(e) => setSlot(slotKey, idx, e.target.value)}
                    className="w-full h-10 px-2 rounded bg-black/60 border border-white/20 text-white text-sm focus:border-[#F4B400] outline-none"
                    data-testid={`seeding-select-${team.id}-${slotKey}-${idx}`}
                  >
                    <option value="" style={{ background: "#0a1611", color: "#e5e7eb" }}>
                      — pick player —
                    </option>
                    {optionsFor(slots[slotKey][idx]).map((o) => (
                      <option
                        key={o.id}
                        value={o.id}
                        style={{ background: "#0a1611", color: "#ffffff" }}
                      >
                        {o.name} · {o.cat}{o.isIntl ? " (INTL)" : ""} · {o.pts}pt
                        {o.assignedIn ? ` — in ${o.assignedIn}` : ""}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
          );
        })}
        {/* Reserve — the 7th player, not assigned to a seed. */}
        <div data-testid={`seeding-slot-${team.id}-reserve`}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">
              Reserve <span className="font-normal text-zinc-500">(optional · 7th player)</span>
            </div>
          </div>
          <select
            value={slots.reserve || ""}
            onChange={(e) => setSlot("reserve", 0, e.target.value)}
            className="w-full h-10 px-2 rounded bg-black/40 border border-white/10 text-zinc-200 text-sm focus:border-[#F4B400] outline-none"
            data-testid={`seeding-select-${team.id}-reserve`}
          >
            <option value="" style={{ background: "#0a1611", color: "#9ca3af" }}>
              — none —
            </option>
            {optionsFor(slots.reserve).map((o) => (
              <option
                key={o.id}
                value={o.id}
                style={{ background: "#0a1611", color: "#ffffff" }}
              >
                {o.name} · {o.cat} · {o.pts}pt{o.assignedIn ? ` — in ${o.assignedIn}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <button
            onClick={clearAll}
            className="uppercase tracking-widest text-zinc-500 hover:text-red-300"
            data-testid={`seeding-clear-btn-${team.id}`}
          >
            Clear all slots
          </button>
          {roster.length > 0 && (
            <span className="text-zinc-500 italic">
              {roster.length} in roster · {roster.length - totalFilled - (slots.reserve ? 1 : 0)}{" "}
              unassigned
            </span>
          )}
        </div>
      </div>

      <Button
        onClick={compute}
        disabled={totalFilled !== 6 || busy}
        className="w-full h-11 bg-[#F4B400] hover:bg-[#e5aa00] text-black font-bold uppercase tracking-widest mb-3 disabled:opacity-40"
        data-testid={`seeding-compute-btn-${team.id}`}
      >
        <Calculator className="w-4 h-4 mr-2" />
        {busy ? "Computing…" : "Auto-Compute Seeds (Re-Sort Slots)"}
      </Button>

      {autoResult && (
        <div className="space-y-2 mb-3" data-testid={`seeding-result-${team.id}`}>
          {autoResult.tied && (
            <div className="p-2 rounded bg-amber-500/15 border border-amber-500/40 flex items-start gap-2 text-xs text-amber-100">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <b>{autoResult.tied}</b> — pair totals are tied. Slots reflect auto-order; adjust
                manually if needed.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Live seed preview from CURRENT slot state — mirrors what Publish sends. */}
      {totalFilled === 6 && (
        <div className="space-y-1.5 mb-3" data-testid={`seeding-live-preview-${team.id}`}>
          {["seed_1", "seed_2", "seed_3"].map((k, i) => (
            <div
              key={k}
              className="flex items-center gap-2 p-2 rounded bg-black/30 border border-white/5"
            >
              <div
                className="w-14 text-[10px] uppercase tracking-widest font-bold"
                style={{ color: ["#F4B400", "#00A86B", "#7DD3FC"][i] }}
              >
                {["Seed 1", "Seed 2", "Seed 3"][i]}
              </div>
              <div className="flex-1 text-xs text-white truncate">
                {slots[k].map(nameOf).join(" + ")}
              </div>
              <div className="text-[10px] uppercase text-[#F4B400] font-bold">
                Total <span className="text-sm tabular-nums">{totalOf(slots[k])}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Override reason (required for audit trail)…"
        className="bg-black/30 border-white/20 text-white text-sm mb-2"
        rows={2}
        data-testid={`seeding-reason-${team.id}`}
      />
      {totalFilled === 6 && (
        <div
          className="mb-2 p-2 rounded bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-100 flex items-start gap-1.5"
          data-testid={`seeding-override-warning-${team.id}`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <b>Publishing overrides the automatic seeding rule.</b> Your manual slot assignment
            will be saved as-is (server won't re-sort). Reason will be attached to the audit log.
          </span>
        </div>
      )}
      <Button
        onClick={publish}
        disabled={totalFilled !== 6 || !reason.trim() || !hasChanged || busy}
        className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-widest disabled:opacity-40"
        data-testid={`seeding-publish-btn-${team.id}`}
      >
        <ShieldCheck className="w-4 h-4 mr-2" />
        {busy ? "Publishing…" : "Publish Override"}
      </Button>
      {/* iter80 · Surface WHY the Publish button is disabled so admins never
       * have to guess. Only rendered when the button is disabled. */}
      {!busy && (totalFilled !== 6 || !reason.trim() || !hasChanged) && (
        <div
          className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"
          data-testid={`seeding-publish-hint-${team.id}`}
        >
          <AlertTriangle className="w-3 h-3 text-zinc-500" />
          <span>
            {totalFilled !== 6
              ? `Fill all 6 seed slots (${totalFilled}/6).`
              : !hasChanged
              ? "No changes to publish — swap or reassign a player first."
              : "Type an override reason to enable publish."}
          </span>
        </div>
      )}
    </Card>
  );
}

export default function SeedingAdmin() {
  const [ties, setTies] = useState([]);
  const [activeTieId, setActiveTieId] = useState(null);
  const [tieDetail, setTieDetail] = useState(null);
  const [filter, setFilter] = useState("scheduled");
  const [loading, setLoading] = useState(true);

  const loadTies = async () => {
    setLoading(true);
    try {
      // Gather every match that has a tie_id + upcoming/scheduled/finished status.
      const { data: matches } = await api.get("/matches");
      const byTie = new Map();
      for (const m of matches || []) {
        if (!m.tie_id) continue;
        if (!byTie.has(m.tie_id)) {
          byTie.set(m.tie_id, {
            tie_id: m.tie_id,
            team_a_id: m.team_a_id,
            team_b_id: m.team_b_id,
            scheduled_at: m.scheduled_at,
            court: m.court,
            stage: m.stage || "group",
            statuses: new Set(),
          });
        }
        byTie.get(m.tie_id).statuses.add(m.status || "scheduled");
      }
      const rows = Array.from(byTie.values()).map((t) => ({
        ...t,
        status: t.statuses.has("live")
          ? "live"
          : Array.from(t.statuses).every((s) => s === "finished")
          ? "finished"
          : "scheduled",
      }));
      // Hydrate team names
      const { data: teams } = await api.get("/teams");
      const teamMap = Object.fromEntries((teams || []).map((t) => [t.id, t]));
      const hydrated = rows.map((r) => ({
        ...r,
        team_a: teamMap[r.team_a_id],
        team_b: teamMap[r.team_b_id],
      }));
      hydrated.sort((a, b) => {
        const bucket = (s) => (s === "live" ? 0 : s === "scheduled" ? 1 : 2);
        return (
          bucket(a.status) - bucket(b.status) ||
          (a.scheduled_at || "").localeCompare(b.scheduled_at || "")
        );
      });
      setTies(hydrated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ties");
    } finally {
      setLoading(false);
    }
  };

  const openTie = async (tie_id) => {
    setActiveTieId(tie_id);
    setTieDetail(null);
    try {
      const { data } = await api.get(`/ties/${tie_id}`);
      setTieDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tie");
    }
  };

  useEffect(() => {
    loadTies();
  }, []);

  const filteredTies = ties.filter((t) => filter === "all" || t.status === filter);

  return (
    <div
      className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white"
      data-testid="seeding-admin-page"
    >
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-[#F4B400]" />
          <h1 className="font-anton text-4xl uppercase tracking-tight">
            Edit Seeding & Scores
          </h1>
        </div>
        <p className="text-sm text-zinc-400 max-w-3xl">
          Super Admin oversight. Pick a tie → override locked lineups (mandatory reason) OR enter /
          fix the final score for each seed match. Standings recalculate live via SSE the moment a
          score is saved.{" "}
          <b className="text-[#F4B400]">Auto-seed rule: lowest pair total = Seed 1.</b>
        </p>
      </div>

      {/* Tie list */}
      <AutoPublishAllButton onDone={loadTies} />

      <Card className="p-4 mb-6 bg-[#0f1a15] border-white/10" data-testid="seeding-tie-list">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            Ties · {filteredTies.length}
          </div>
          <div className="flex gap-1">
            {["scheduled", "live", "finished", "all"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`seeding-filter-${f}`}
                className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${
                  filter === f
                    ? "bg-[#F4B400] text-black"
                    : "bg-black/30 text-zinc-400 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-xs italic text-zinc-500 py-4 text-center">Loading ties…</div>
        ) : filteredTies.length === 0 ? (
          <div className="text-xs italic text-zinc-500 py-4 text-center">
            No ties for this filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {filteredTies.map((t) => {
              const isActive = activeTieId === t.tie_id;
              return (
                <button
                  key={t.tie_id}
                  onClick={() => openTie(t.tie_id)}
                  data-testid={`seeding-tie-${t.tie_id}`}
                  className={`text-left p-3 rounded border transition ${
                    isActive
                      ? "bg-[#F4B400]/10 border-[#F4B400]"
                      : "bg-black/30 border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">
                      {t.stage}
                    </span>
                    <span
                      className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        t.status === "live"
                          ? "bg-red-500/20 text-red-300"
                          : t.status === "finished"
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-zinc-500/20 text-zinc-300"
                      }`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div className="text-sm text-white leading-tight">
                    {t.team_a?.name || "TBD"} <span className="text-zinc-500">vs</span>{" "}
                    {t.team_b?.name || "TBD"}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {t.scheduled_at ? new Date(t.scheduled_at).toLocaleString() : "Unscheduled"}
                    {t.court ? ` · ${t.court}` : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Active tie — team panels */}
      {activeTieId && tieDetail && (
        <div data-testid="seeding-tie-detail">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#F4B400]" />
            <h2 className="font-anton text-2xl uppercase text-white">
              {tieDetail.team_a?.name} vs {tieDetail.team_b?.name}
            </h2>
            <Link
              to={`/tie/${activeTieId}`}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#F4B400] flex items-center gap-1 ml-2"
            >
              Public Tie <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TeamPanel
              tieId={activeTieId}
              team={tieDetail.team_a}
              matches={tieDetail.matches}
              onPublished={() => openTie(activeTieId)}
            />
            <TeamPanel
              tieId={activeTieId}
              team={tieDetail.team_b}
              matches={tieDetail.matches}
              onPublished={() => openTie(activeTieId)}
            />
          </div>

          <MatchScoresPanel
            tieDetail={tieDetail}
            onScoreSaved={() => openTie(activeTieId)}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Bulk-op: auto-publish lineups for every upcoming tie whose team hasn't yet
 * submitted. NEVER overwrites owner submissions — owners retain the right to
 * submit their own. Hits POST /api/admin/lineups/auto-publish-all.
 */
function AutoPublishAllButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);

  useEffect(() => {
    if (undoSecondsLeft <= 0) return;
    const t = setInterval(() => {
      setUndoSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [undoSecondsLeft]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/lineups/auto-publish-all?stage=group");
      setLastResult(data);
      toast.success(
        `Auto-Publish: ${data.filled} filled · ${data.skipped_already_submitted} preserved · ${data.skipped_no_roster} skipped (roster < 6)`
      );
      // Start the 60-second Undo countdown only if we actually filled anything.
      if (data.filled > 0 && data.batch_id) {
        setUndoSecondsLeft(data.undo_window_seconds || 60);
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-publish failed");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const undo = async () => {
    if (!lastResult?.batch_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/lineups/auto-publish-undo/${lastResult.batch_id}`);
      toast.success(`Undone — reversed ${data.undone} auto-filled lineup(s).`);
      setUndoSecondsLeft(0);
      setLastResult({ ...lastResult, undone: data.undone });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="p-4 mb-4 bg-gradient-to-r from-[#0f1a15] to-[#1a2a1f] border-[#F4B400]/30"
      data-testid="auto-publish-all-card"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F4B400]/20 border border-[#F4B400]/40 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-[#F4B400]" />
          </div>
          <div>
            <div className="font-anton text-lg uppercase tracking-tight text-white">
              Auto-Publish All Ties
            </div>
            <div className="text-xs text-zinc-400 mt-0.5 max-w-2xl">
              One-click: fill every upcoming league tie's lineup using the balanced-pairing engine
              (lowest pair total = Seed 1). <b className="text-[#F4B400]">Owner submissions are never overwritten</b> —
              this only fills team slots that haven't been submitted yet. You have a 60-second Undo window after publishing.
            </div>
          </div>
        </div>
        {confirming ? (
          <div className="flex items-center gap-2">
            <Button
              onClick={run}
              disabled={busy}
              className="bg-[#F4B400] hover:bg-[#e5aa00] text-black font-bold uppercase tracking-widest"
              data-testid="auto-publish-confirm-btn"
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              {busy ? "Publishing…" : "Confirm — Publish All"}
            </Button>
            <button
              onClick={() => setConfirming(false)}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
              data-testid="auto-publish-cancel-btn"
            >
              Cancel
            </button>
          </div>
        ) : (
          <Button
            onClick={() => setConfirming(true)}
            disabled={busy}
            size="sm"
            className="bg-[#F4B400] hover:bg-[#e5aa00] text-black font-bold uppercase tracking-widest shrink-0"
            data-testid="auto-publish-open-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            Auto-Publish All
          </Button>
        )}
      </div>
      {lastResult && (
        <div
          className="mt-3 p-2 rounded bg-black/40 border border-white/10 text-[11px] text-zinc-300 flex items-center justify-between gap-3 flex-wrap"
          data-testid="auto-publish-last-result"
        >
          <div>
            <b className="text-emerald-300">✓ Last run:</b>{" "}
            {lastResult.filled} filled ·{" "}
            <span className="text-amber-300">{lastResult.skipped_already_submitted}</span> already
            submitted (preserved) ·{" "}
            <span className="text-red-300">{lastResult.skipped_no_roster}</span> skipped (roster {"<"} 6)
            {lastResult.undone !== undefined && (
              <span className="ml-2 text-amber-300">
                · <b>{lastResult.undone}</b> undone
              </span>
            )}
            {lastResult.errors?.length > 0 && (
              <div className="mt-1 text-red-300">
                {lastResult.errors.length} error(s):{" "}
                {lastResult.errors.slice(0, 3).map((e, i) => (
                  <span key={i}>
                    {e.team_name || e.team_id?.slice(0, 8)} ({e.reason}){i < 2 ? " · " : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
          {undoSecondsLeft > 0 && lastResult.filled > 0 && (
            <div className="flex items-center gap-2" data-testid="auto-publish-undo-slot">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold tabular-nums">
                Undo window: {undoSecondsLeft}s
              </div>
              <Button
                onClick={undo}
                disabled={busy}
                size="sm"
                variant="outline"
                className="border-amber-500/60 text-amber-200 hover:bg-amber-500/10 font-bold uppercase tracking-widest text-[10px] h-7 px-2"
                data-testid="auto-publish-undo-btn"
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Undo
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Season 3 rulebook — number of sets per seed match by stage.
 *   Group / round robin  → 2 sets
 *   Quarter / Semi / Final → 3 sets
 */
const STAGE_SETS = { group: 2, quarter: 3, quarterfinal: 3, qf: 3, semifinal: 3, semi: 3, sf: 3, final: 3, ko: 3 };
const setsForStage = (stage) => STAGE_SETS[(stage || "group").toLowerCase()] || 2;

const emptySet = () => ({ a: 0, b: 0 });
const hydrateSets = (m) => {
  const n = setsForStage(m.stage);
  const existing = Array.isArray(m.set_scores) ? m.set_scores : [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const s = existing[i];
    out.push({ a: Number(s?.a) || 0, b: Number(s?.b) || 0 });
  }
  return out;
};
const setsWon = (sets) => {
  let a = 0, b = 0;
  for (const s of sets) {
    const sa = Number(s?.a) || 0;
    const sb = Number(s?.b) || 0;
    if (sa === 0 && sb === 0) continue;
    if (sa > sb) a++;
    else if (sb > sa) b++;
  }
  return { a, b };
};

/**
 * Super Admin match score entry panel — one row per seed match in the tie.
 * Writes to POST /api/admin/matches/{id}/final-score. Backend fires
 * MATCH_FINALIZED on the event bus so public standings / bracket / schedule
 * SSE listeners refresh immediately. Editing a finished match REQUIRES a
 * reason (audit trail).
 */
function MatchScoresPanel({ tieDetail, onScoreSaved }) {
  const matches = useMemo(() => {
    const list = tieDetail?.matches || [];
    return [...list].sort(
      (a, b) => (a.seed || a.seed_number || 999) - (b.seed || b.seed_number || 999)
    );
  }, [tieDetail]);

  // Panel-level score state, keyed by match id — lets one "Save Scores" button
  // fire the endpoint for every dirty row in parallel.
  const [scores, setScores] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Reset local state whenever the tie changes / gets refreshed.
    const initial = {};
    for (const m of matches) {
      initial[m.id] = {
        sets: hydrateSets(m),
        dirty: false,
        finished: (m.status || "").toLowerCase() === "finished",
      };
    }
    setScores(initial);
    setReason("");
  }, [tieDetail]);

  const setSetScore = (mid, idx, side, v) => {
    setScores((prev) => {
      const cur = prev[mid] || { sets: [], dirty: false, finished: false };
      const nextSets = cur.sets.map((s, i) =>
        i === idx ? { ...s, [side]: v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0) } : s
      );
      return { ...prev, [mid]: { ...cur, sets: nextSets, dirty: true } };
    });
  };
  const resetEntry = (mid) => {
    const m = matches.find((x) => x.id === mid);
    setScores((prev) => ({
      ...prev,
      [mid]: {
        sets: hydrateSets(m),
        finished: (m.status || "").toLowerCase() === "finished",
        dirty: false,
      },
    }));
  };
  const applySetPreset = (mid, setIdx, a, b) => {
    setScores((prev) => {
      const cur = prev[mid] || { sets: [], dirty: false, finished: false };
      const nextSets = cur.sets.map((s, i) => (i === setIdx ? { a, b } : s));
      return { ...prev, [mid]: { ...cur, sets: nextSets, dirty: true } };
    });
  };

  const dirtyMatches = matches.filter((m) => scores[m.id]?.dirty);
  const overwritingAny = dirtyMatches.some((m) => scores[m.id]?.finished);
  const needsReason = overwritingAny && !reason.trim();

  // Tie preview from the CURRENT set inputs — seed-match wins per team.
  const tiePreview = useMemo(() => {
    if (matches.length === 0) return null;
    let aWins = 0, bWins = 0, ties = 0;
    for (const m of matches) {
      const s = scores[m.id];
      if (!s) continue;
      const w = setsWon(s.sets);
      if (w.a === 0 && w.b === 0) continue;
      if (w.a > w.b) aWins++;
      else if (w.b > w.a) bWins++;
      else ties++;
    }
    return { aWins, bWins, ties };
  }, [scores, matches]);

  if (!matches.length) return null;

  const saveAll = async () => {
    if (dirtyMatches.length === 0) {
      toast.info("No changes to save.");
      return;
    }
    if (needsReason) {
      toast.error("Reason required to overwrite one or more finalised matches.");
      return;
    }
    // Validate every dirty entry — every set has non-negative ints, at least
    // one set has a non-zero score (otherwise nothing to save meaningfully).
    for (const m of dirtyMatches) {
      const s = scores[m.id];
      const anyScored = s.sets.some((x) => (Number(x.a) || 0) + (Number(x.b) || 0) > 0);
      if (!anyScored) {
        toast.error(`Match ${m.id.slice(0, 8)}: enter at least one set score.`);
        return;
      }
    }
    setBusy(true);
    // Standings BEFORE — capture only the two teams involved in this tie.
    const teamAId = tieDetail?.team_a?.id;
    const teamBId = tieDetail?.team_b?.id;
    let before = { a: null, b: null };
    try {
      const { data } = await api.get("/public/standings");
      const rows = data || [];
      before.a = rows.find((r) => r.id === teamAId) || null;
      before.b = rows.find((r) => r.id === teamBId) || null;
    } catch {
      /* non-fatal — diff toast just won't fire */
    }
    const results = await Promise.allSettled(
      dirtyMatches.map((m) => {
        const s = scores[m.id];
        const w = setsWon(s.sets);
        // Backend re-computes score_a/score_b from set_scores when a real
        // multi-set breakdown is submitted, but we send sets-won explicitly
        // so single-set-legacy safety paths behave correctly.
        return api.post(`/admin/matches/${m.id}/final-score`, {
          score_a: w.a,
          score_b: w.b,
          set_scores: s.sets.map((x) => ({ a: Number(x.a) || 0, b: Number(x.b) || 0 })),
          reason: reason.trim() || undefined,
        });
      })
    );
    setBusy(false);
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    if (failed === 0) {
      // Standings AFTER — compute the diff and render a rich toast.
      try {
        const { data: fresh } = await api.get("/public/standings");
        const after = {
          a: (fresh || []).find((r) => r.id === teamAId) || null,
          b: (fresh || []).find((r) => r.id === teamBId) || null,
        };
        const fmtDelta = (row, prev, key, label) => {
          if (!row || !prev) return "";
          const d = (row[key] ?? 0) - (prev[key] ?? 0);
          if (d === 0) return "";
          const sign = d > 0 ? "+" : "";
          return `${sign}${d} ${label}`;
        };
        const teamLine = (row, prev, name) => {
          if (!row || !prev) return `${name}: —`;
          const parts = [
            fmtDelta(row, prev, "won", "W"),
            fmtDelta(row, prev, "lost", "L"),
            fmtDelta(row, prev, "for", "SF"),
            fmtDelta(row, prev, "against", "SA"),
          ].filter(Boolean);
          const gd = row.game_diff ?? row.for - row.against;
          const gdPrev = prev.game_diff ?? prev.for - prev.against;
          const gdDelta = gd - gdPrev;
          const gdStr =
            gdDelta === 0
              ? ""
              : ` · SD ${gdPrev > 0 ? "+" : ""}${gdPrev} → ${gd > 0 ? "+" : ""}${gd}`;
          return `${name}: ${parts.join(" · ") || "no change"}${gdStr}`;
        };
        toast.success(
          `Saved ${ok} match score${ok === 1 ? "" : "s"} · standings recalculated`,
          {
            description: (
              <div
                className="text-[11px] leading-relaxed"
                data-testid="admin-match-save-diff-toast"
              >
                <div>{teamLine(after.a, before.a, tieDetail?.team_a?.name || "Team A")}</div>
                <div>{teamLine(after.b, before.b, tieDetail?.team_b?.name || "Team B")}</div>
              </div>
            ),
            duration: 8000,
          }
        );
      } catch {
        toast.success(
          `Saved ${ok} match score${ok === 1 ? "" : "s"} · standings recalculated`
        );
      }
    } else {
      const firstErr =
        results.find((r) => r.status === "rejected")?.reason?.response?.data?.detail ||
        "Some saves failed";
      toast.error(`${ok}/${results.length} saved · ${firstErr}`);
    }
    setReason("");
    onScoreSaved?.();
  };

  const teamAName = tieDetail?.team_a?.name || "Team A";
  const teamBName = tieDetail?.team_b?.name || "Team B";
  const tieWinner =
    tiePreview && tiePreview.aWins > tiePreview.bWins
      ? teamAName
      : tiePreview && tiePreview.bWins > tiePreview.aWins
      ? teamBName
      : null;

  return (
    <Card
      className="p-5 mt-6 bg-[#0f1a15] border-white/10"
      data-testid="admin-match-scores-panel"
    >
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Target className="w-5 h-5 text-[#F4B400]" />
        <h3 className="font-anton text-2xl uppercase text-white">Enter Scores</h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
          Group ties: 2 sets · KO ties: 3 sets · standings recalc on save
        </span>
      </div>

      <div className="space-y-3">
        {matches.map((m) => (
          <MatchScoreRow
            key={m.id}
            match={m}
            teamA={tieDetail.team_a}
            teamB={tieDetail.team_b}
            entry={scores[m.id] || { sets: hydrateSets(m), dirty: false, finished: false }}
            onSetScore={(idx, side, v) => setSetScore(m.id, idx, side, v)}
            onSetPreset={(idx, a, b) => applySetPreset(m.id, idx, a, b)}
            onReset={() => resetEntry(m.id)}
          />
        ))}
      </div>

      {/* Live tie preview */}
      {(tiePreview?.aWins > 0 || tiePreview?.bWins > 0) && (
        <div
          className="mt-4 p-3 rounded-lg border border-[#F4B400]/40 bg-[#F4B400]/5 flex items-center justify-between"
          data-testid="admin-tie-preview"
        >
          <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold">
            Tie Result (from inputs)
          </div>
          <div className="text-sm text-white">
            <b>{teamAName}</b>{" "}
            <span className="font-anton text-xl tabular-nums text-[#F4B400]">
              {tiePreview.aWins}
            </span>
            <span className="text-zinc-500 mx-2">—</span>
            <span className="font-anton text-xl tabular-nums text-[#00A86B]">
              {tiePreview.bWins}
            </span>{" "}
            <b>{teamBName}</b>
            {tieWinner && (
              <span className="ml-3 text-[10px] uppercase font-bold text-emerald-300">
                Winner: {tieWinner}
              </span>
            )}
          </div>
        </div>
      )}

      {overwritingAny && (
        <div
          className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/40 text-[11px] text-amber-100 flex items-start gap-1.5"
          data-testid="admin-match-overwrite-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            <b>One or more matches are already finalised.</b> Provide a reason before saving — it'll
            be attached to the audit log for every overwritten match.
          </span>
        </div>
      )}

      {(dirtyMatches.length > 0 || overwritingAny) && (
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            overwritingAny
              ? "Reason for overwriting finalised match(es) — required…"
              : "Optional note for the audit log…"
          }
          rows={1}
          className="bg-black/40 border-white/20 text-white text-xs mt-3"
          data-testid="admin-match-save-all-reason"
        />
      )}

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <Button
          onClick={saveAll}
          disabled={busy || dirtyMatches.length === 0 || needsReason}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-widest disabled:opacity-40"
          data-testid="admin-match-save-all"
        >
          <Save className="w-4 h-4 mr-2" />
          {busy
            ? `Saving ${dirtyMatches.length}…`
            : `Save Scores (${dirtyMatches.length} match${
                dirtyMatches.length === 1 ? "" : "es"
              })`}
        </Button>
        {dirtyMatches.length > 0 && (
          <button
            onClick={() => {
              for (const m of dirtyMatches) resetEntry(m.id);
              setReason("");
            }}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1"
            data-testid="admin-match-reset-all"
          >
            <RotateCcw className="w-3 h-3" /> Reset all
          </button>
        )}
      </div>
    </Card>
  );
}

function MatchScoreRow({
  match,
  teamA,
  teamB,
  entry,
  onSetScore,
  onSetPreset,
  onReset,
}) {
  const dirty = entry.dirty;
  const teamAForMatch =
    match.team_a_id === teamA?.id ? teamA : match.team_a_id === teamB?.id ? teamB : { name: "TBD" };
  const teamBForMatch =
    match.team_b_id === teamB?.id ? teamB : match.team_b_id === teamA?.id ? teamA : { name: "TBD" };

  const seedNum = match.seed || match.seed_number;
  const seedLabel = seedNum ? `Seed ${seedNum}` : match.stage || "Match";
  const seedAccent =
    seedNum === 1 ? "#F4B400" : seedNum === 2 ? "#00A86B" : seedNum === 3 ? "#7DD3FC" : "#94a3b8";
  const statusColor =
    match.status === "finished"
      ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
      : match.status === "live"
      ? "text-red-300 bg-red-500/10 border-red-500/30"
      : "text-zinc-300 bg-white/5 border-white/10";

  const sets = entry.sets || [];
  const numSets = sets.length;
  const w = setsWon(sets);
  const stageLabel = (match.stage || "group").toUpperCase();

  const matchWinner =
    w.a === w.b ? null : w.a > w.b ? teamAForMatch.name : teamBForMatch.name;

  return (
    <div
      className="p-3 rounded-lg border bg-black/30"
      style={{ borderColor: seedNum ? `${seedAccent}55` : "rgba(255,255,255,0.10)" }}
      data-testid={`admin-match-row-${match.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className="font-anton text-2xl uppercase tracking-tight leading-none"
            style={{ color: seedAccent }}
            data-testid={`admin-match-seed-label-${match.id}`}
          >
            {seedLabel}
          </div>
          <span
            className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 border border-white/10"
            data-testid={`admin-match-stage-${match.id}`}
          >
            {stageLabel} · Best of {numSets}
          </span>
          <span
            className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${statusColor}`}
            data-testid={`admin-match-status-${match.id}`}
          >
            {match.status || "scheduled"}
          </span>
          {match.court && <span className="text-[10px] text-zinc-500">· {match.court}</span>}
          {dirty && (
            <span
              className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-[#F4B400]/20 text-[#F4B400]"
              data-testid={`admin-match-dirty-${match.id}`}
            >
              unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {dirty && (
            <button
              onClick={onReset}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1"
              data-testid={`admin-match-reset-${match.id}`}
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          )}
          <Link
            to={`/matches/${match.id}`}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#F4B400] flex items-center gap-1"
          >
            Match Page <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Team header row — names + running sets-won summary */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-3">
        <div className="text-right">
          <div className="text-sm text-white font-semibold truncate">{teamAForMatch.name || "TBD"}</div>
          <div
            className="font-anton text-4xl leading-none tabular-nums text-[#F4B400]"
            data-testid={`admin-match-sets-a-${match.id}`}
          >
            {w.a}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Sets won</div>
        </div>
        <div className="text-2xl font-anton text-zinc-500">—</div>
        <div>
          <div className="text-sm text-white font-semibold truncate">{teamBForMatch.name || "TBD"}</div>
          <div
            className="font-anton text-4xl leading-none tabular-nums text-[#00A86B]"
            data-testid={`admin-match-sets-b-${match.id}`}
          >
            {w.b}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Sets won</div>
        </div>
      </div>
      {matchWinner && (
        <div
          className="mb-3 text-center text-[10px] uppercase tracking-widest font-bold text-emerald-300"
          data-testid={`admin-match-winner-${match.id}`}
        >
          Match Winner: {matchWinner}
        </div>
      )}

      {/* Per-set inputs — 2 rows for Group, 3 rows for KO */}
      <div className="space-y-2" data-testid={`admin-match-sets-${match.id}`}>
        {sets.map((s, idx) => (
          <SetRow
            key={idx}
            index={idx}
            match={match}
            a={s.a}
            b={s.b}
            teamAName={teamAForMatch.name}
            teamBName={teamBForMatch.name}
            onChange={(side, v) => onSetScore(idx, side, v)}
            onPreset={(pa, pb) => onSetPreset(idx, pa, pb)}
          />
        ))}
      </div>
    </div>
  );
}

/** One per-set row: `Set N: [A] vs [B]` + straight-set presets. */
function SetRow({ index, match, a, b, teamAName, teamBName, onChange, onPreset }) {
  return (
    <div
      className="p-2 rounded border border-white/5 bg-black/40"
      data-testid={`admin-match-set-row-${match.id}-${index + 1}`}
    >
      <div className="grid grid-cols-[80px_1fr_auto_1fr] items-center gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">
          Set {index + 1}
        </div>
        <Input
          type="number"
          min={0}
          value={a}
          onChange={(e) => onChange("a", e.target.value)}
          className="h-11 text-2xl font-anton text-center bg-black/60 border-white/15 text-white tabular-nums"
          data-testid={`admin-match-set-a-${match.id}-${index + 1}`}
        />
        <div className="text-lg font-anton text-zinc-500 px-1">—</div>
        <Input
          type="number"
          min={0}
          value={b}
          onChange={(e) => onChange("b", e.target.value)}
          className="h-11 text-2xl font-anton text-center bg-black/60 border-white/15 text-white tabular-nums"
          data-testid={`admin-match-set-b-${match.id}-${index + 1}`}
        />
      </div>
      {/* Compact straight-set presets — one row of shortcuts per set. */}
      <div
        className="flex items-center gap-1 flex-wrap pl-[88px]"
        data-testid={`admin-match-set-presets-${match.id}-${index + 1}`}
      >
        {[
          [6, 0], [6, 1], [6, 2], [6, 3], [6, 4], [7, 5], [7, 6],
        ].map(([hi, lo]) => (
          <React.Fragment key={`${hi}-${lo}`}>
            <button
              onClick={() => onPreset(hi, lo)}
              className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded border border-[#F4B400]/30 bg-[#F4B400]/5 hover:bg-[#F4B400]/15 text-[#F4B400]"
              title={`${teamAName} wins ${hi}-${lo}`}
              data-testid={`admin-match-set-preset-a-${match.id}-${index + 1}-${hi}-${lo}`}
            >
              {hi}-{lo}
            </button>
            <button
              onClick={() => onPreset(lo, hi)}
              className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded border border-[#00A86B]/30 bg-[#00A86B]/5 hover:bg-[#00A86B]/15 text-[#00A86B]"
              title={`${teamBName} wins ${hi}-${lo}`}
              data-testid={`admin-match-set-preset-b-${match.id}-${index + 1}-${hi}-${lo}`}
            >
              {lo}-{hi}
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
